import React, { useState, useEffect } from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { Button } from './ui/button';

const HeroV2 = () => {
  const [currentStat, setCurrentStat] = useState(0);
  
  const stats = [
    { value: '10,000+', label: 'Dreams Fulfilled', color: 'from-[#8BC34A] to-[#7CB342]' },
    { value: '95%', label: 'Success Rate', color: 'from-[#FF7043] to-[#F4511E]' },
    { value: '25+', label: 'Countries', color: 'from-[#1565C0] to-[#0D47A1]' },
    { value: '15+', label: 'Years Experience', color: 'from-[#FF7043] to-[#8BC34A]' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % stats.length);
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden bg-gradient-to-br from-white via-gray-50 to-blue-50">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#8BC34A]/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#1565C0]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-[#FF7043]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* Badge - Hidden on mobile, shown on tablet+ */}
            <div className="hidden md:flex items-center gap-2 bg-white shadow-lg rounded-full px-6 py-3 mb-6 border border-gray-100 w-fit">
              <div className="w-2 h-2 bg-[#8BC34A] rounded-full animate-pulse flex-shrink-0"></div>
              <span className="text-gray-700 text-sm font-semibold">Trusted Immigration & Education Partner</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Your Journey to
              <span className="block mt-2 bg-gradient-to-r from-[#1565C0] via-[#FF7043] to-[#8BC34A] bg-clip-text text-transparent">
                Global Success
              </span>
              Starts Here
            </h1>

            {/* Subheading */}
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
              Transform your dreams of studying, working, or settling abroad into reality with personalized guidance from India's most trusted consultants.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button 
                onClick={() => scrollToSection('consultation')}
                size="lg"
                className="bg-gradient-to-r from-[#1565C0] to-[#0D47A1] hover:from-[#0D47A1] hover:to-[#1565C0] text-white px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 group"
              >
                Start Your Journey Free
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              <Button 
                onClick={() => scrollToSection('process')}
                size="lg"
                variant="outline"
                className="border-2 border-[#1565C0] text-[#1565C0] hover:bg-[#1565C0] hover:text-white px-8 py-6 text-lg font-semibold transition-all duration-300 group"
              >
                <Play className="mr-2 group-hover:scale-110 transition-transform" size={20} />
                How It Works
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex -space-x-3">
                <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330" alt="Student" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d" alt="Student" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80" alt="Student" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                <div className="w-10 h-10 rounded-full border-2 border-white bg-gradient-to-r from-[#FF7043] to-[#F4511E] flex items-center justify-center text-white text-xs font-bold">
                  10K+
                </div>
              </div>
              <p className="text-sm text-gray-600">
                <span className="font-bold text-gray-900">10,000+ students</span> have achieved their dreams with us
              </p>
            </div>
          </div>

          {/* Right Content - Animated Stats & Image */}
          <div className="relative">
            {/* Main Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1747835746625-84a0d8387dee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxpbnRlcm5hdGlvbmFsJTIwc3R1ZGVudHMlMjBncmFkdWF0aW9ufGVufDB8fHx8MTc3NTg4OTUwN3ww&ixlib=rb-4.1.0&q=85"
                alt="International Students Success"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>

            {/* Floating Stat Cards */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-2xl p-6 max-w-xs animate-fade-in">
              <div className={`inline-block bg-gradient-to-r ${stats[currentStat].color} px-4 py-2 rounded-lg mb-3`}>
                <span className="text-white font-bold text-sm">Live Update</span>
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-1">{stats[currentStat].value}</div>
              <div className="text-gray-600">{stats[currentStat].label}</div>
              <div className="flex gap-1 mt-4">
                {stats.map((_, index) => (
                  <div 
                    key={index} 
                    className={`h-1 rounded-full transition-all duration-300 ${
                      index === currentStat ? 'w-8 bg-[#1565C0]' : 'w-2 bg-gray-300'
                    }`}
                  ></div>
                ))}
              </div>
            </div>

            {/* Certification Badge */}
            <div className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl p-4 animate-fade-in" style={{ animationDelay: '0.5s' }}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#8BC34A] to-[#7CB342] flex items-center justify-center mx-auto mb-2">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <div className="text-xs font-bold text-gray-900">Certified</div>
                <div className="text-xs text-gray-600">Professionals</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="flex flex-col items-center gap-2">
          <span className="text-sm text-gray-600 font-medium">Scroll to explore</span>
          <svg className="w-6 h-6 text-[#1565C0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </section>
  );
};

export default HeroV2;
